/**
 * Modèles liés à la sécurité du compte (sessions actives + historique de connexion).
 *
 * Le back-end expose ces données via des endpoints qui proxifient Keycloak
 * (plugin follow-sessions pour les sessions actives, Admin Events API pour l'historique) :
 *  - GET    /users/me/sessions          -> sessions actives, regroupées par device
 *  - DELETE /users/me/sessions          -> déconnexion de toutes les sessions
 *  - DELETE /users/me/sessions/{id}     -> déconnexion d'une session précise
 *  - GET    /users/me/sessions/history  -> historique des connexions (succès/échecs)
 *
 * Les types sont volontairement tolérants (champs optionnels) car les données
 * proviennent de Keycloak et peuvent varier légèrement selon la version.
 */

/** Client OIDC rattaché à une session (format renvoyé par l'API Follow). */
export interface UserSessionClient {
  clientId: string
  clientName?: string | null
}

/** Une session brute (un "client set" pour un device donné). */
export interface UserSession {
  id: string
  ipAddress: string
  browser?: string
  started: string | number
  lastAccess: string | number
  clients: UserSessionClient[] | Record<string, string> | string[]
  current: boolean
}

/** Sessions regroupées par device (navigateur + OS + type d'appareil). */
export interface UserSessionDevice {
  browser?: string
  os: string
  osVersion: string
  device: string
  mobile: boolean
  ipAddress?: string
  lastAccess: string | number
  sessions: UserSession[]
}

export enum SessionHistoryEventType {
  Login = 'LOGIN',
  LoginError = 'LOGIN_ERROR',
  Logout = 'LOGOUT',
  LogoutError = 'LOGOUT_ERROR',
}

/** Un évènement de l'historique des connexions. */
export interface SessionHistoryEvent {
  time: string | number
  type: SessionHistoryEventType | string
  clientId: string
  ipAddress: string
  sessionId: string
  error?: string
  details?: Record<string, unknown>
}

/** Filtre de statut pour l'historique. */
export enum SessionHistoryStatusFilter {
  All = 'all',
  Success = 'success',
  Error = 'error',
}

export interface SessionHistoryFilters {
  status: SessionHistoryStatusFilter
  dateFrom: Date | null
  dateTo: Date | null
}

export const isErrorEvent = (event: SessionHistoryEvent): boolean =>
  event.type === SessionHistoryEventType.LoginError ||
  event.type === SessionHistoryEventType.LogoutError
